import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router';
import { ChangeEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { type ProductItem, validate as validateProductItem } from '../../models/ProductItem';
import { Autocomplete } from '@mui/material';
import { Item, UnitType, unitTypeToUnits } from '../../models/Item';
import NumberField from '../NumberField';

export interface ProductItemFormState {
  values: Partial<Omit<ProductItem, 'id'>>;
  errors: Partial<Record<keyof ProductItemFormState['values'], string>>;
}

export interface ProductItemFormProps {
  items: Item[];
  initialValue?: ProductItem;
  initialErrors?: Partial<Record<keyof ProductItemFormState['values'], string>>;
  onReset?: (formValues: Partial<ProductItemFormState['values']>) => void;
  onFormValuesUpdate?: (formValues: Partial<ProductItemFormState['values']>) => void;
  submitButtonLabel?: string;
  backButtonPath?: string;
  showActionButtons?: boolean;
}

export const newProductItem = {
  id: 0,
  quantity: 0,
  notes: ''
} as ProductItem;

export default function ProductItemForm(props: ProductItemFormProps) {
  const {
    items,
    initialValue,
    initialErrors = {},
    onReset = () => { },
    onFormValuesUpdate = () => { },
    submitButtonLabel = 'Create',
    backButtonPath,
    showActionButtons = true,
  } = props;

  const [errors, setErrors] = useState<Partial<Record<keyof ProductItemFormState['values'], string>>>({});

  const navigate = useNavigate();

  const getUnits = useCallback((selectedItemId: number | undefined) => {
    if ((selectedItemId || 0) > 0) {
      const selectedItem = items.find(item => +item.id === selectedItemId);

      if (selectedItem) {
        return unitTypeToUnits(selectedItem.unitType as UnitType);
      }
    }

    return '';
  }, [items]);

  const handleFormFieldChange = useCallback(
    (name: keyof ProductItemFormState['values'], value: FormFieldValue) => {
      const validateField = (values: Partial<ProductItemFormState['values']>) => {
        const { issues } = validateProductItem(values);

        const issue = issues?.find((issue) => issue.path?.[0] === name);

        if (issue) {
          setErrors({
            ...errors,
            [name]: issue.message,
          });
          return false;
        }

        return true;
      };

      const newFormValues = { ...initialValue, [name]: value };

      const valid = validateField(newFormValues);

      if (valid) {
        onFormValuesUpdate(newFormValues);
      }
    },
    [initialValue, errors, onFormValuesUpdate],
  );

  const handleNumberFieldChange = useCallback(
    (value: number | null, fieldName: string) => {
      handleFormFieldChange(
        fieldName as keyof ProductItemFormState['values'],
        value,
      );
    },
    [handleFormFieldChange],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleFormFieldChange(
        event.target.name as keyof ProductItemFormState['values'],
        event.target.value,
      );
    },
    [handleFormFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(initialValue || newProductItem);
    }
  }, [initialValue, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/items');
  }, [navigate, backButtonPath]);

  return (
    <Box
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 4 }}>
            <Autocomplete
              getOptionLabel={(option) => option.name}
              options={items || []}
              value={initialValue?.item}
              onChange={(_, newValue: Item | null) => {
                handleFormFieldChange(
                  'itemId',
                  +(newValue?.id || 0)
                );
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Item"
                  error={!!errors.itemId || !!initialErrors.itemId}
                  helperText={(errors.itemId || initialErrors.itemId) ?? ' '}
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              )}
              renderOption={(props, item) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                  >
                    {item.name}
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 4 }}>
            <NumberField
              required
              value={initialValue?.quantity || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'quantity')}
              name="quantity"
              label="Quantity"
              error={!!errors.quantity || !!initialErrors.quantity}
              helperText={(errors.quantity || initialErrors.quantity) ?? ''}
              weightFieldUnits={getUnits(initialValue?.itemId)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 4 }}>
            <TextField
              value={initialValue?.notes || ''}
              onChange={handleTextFieldChange}
              slotProps={{ inputLabel: { shrink: true } }}
              name="notes"
              label="Notes"
              fullWidth
            />
          </Grid>
        </Grid>
      </FormGroup>
      {showActionButtons ? (
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="success"
            startIcon={<SaveIcon />}
          >
            {submitButtonLabel}
          </Button>
        </Stack>
      ) : <></>}
    </Box>
  );
}
